import React, { useEffect } from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';

/**
 * Help Center / Docs — public page explaining every live feature.
 * Standard docs layout: sticky category sidebar + content. Content is data-driven
 * (SECTIONS) so it's easy to keep in sync as features change. Covers LIVE features
 * only (no storefront/selling — that's not in production yet).
 */

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    intro: 'CreatorKhata is the all-in-one business app for Indian creators — from brand pitch to paid invoice, in one place. Here’s how to get going.',
    features: [
      {
        name: 'What CreatorKhata does',
        how: [
          'Tracks your brand deals, invoices, payments and TDS in one place — no more scattered spreadsheets and WhatsApp chats.',
          'Gives you AI tools to plan and write content, plus free calculators for pricing and taxes.',
          'Built India-first: GST-compliant invoices, TDS sections, and the MSME 45-day payment rule.',
        ],
        tip: 'It’s free to use right now — just sign up and start.',
      },
      {
        name: 'Sign up & verify your email',
        where: 'Click “Get Started” on the homepage.',
        how: [
          'Sign up with your email, then click the verification link we email you.',
          'Once verified, you land on your Dashboard.',
        ],
        tip: 'Didn’t get the email? Check spam, or use “Forgot password” to re-trigger it.',
      },
      {
        name: 'Finding your way around',
        where: 'Left sidebar (after you log in).',
        how: [
          'The Dashboard is your home base; every feature opens from the left sidebar.',
        ],
        tip: 'Click any stat card on the Dashboard to jump straight to that section.',
      },
    ],
  },
  {
    id: 'business',
    title: 'Business for Creators',
    intro: 'The money side of your creator business — deals, invoices, payments, contracts and your brand-pitch assets.',
    features: [
      {
        name: 'Brand Deals',
        where: '/deals · Sidebar → Brand Deals',
        how: [
          'Click “+ New Deal” and fill in brand name, amount, platform, deal type, contact, deadline and deliverables.',
          'Move each deal through INQUIRY → NEGOTIATING → CONFIRMED → DELIVERED → PAID from the card dropdown.',
          'Status chips at the top filter the list and show your confirmed pipeline value.',
        ],
        tip: 'Click a status chip to filter to just those deals; click again to clear.',
      },
      {
        name: 'GST Invoices',
        where: '/invoices · Sidebar → GST Invoices',
        how: [
          '“+ New Invoice” captures your details, the brand, service description, amount and GST rate (0–28%).',
          'It auto-calculates CGST+SGST (same state) or IGST (inter-state) with a live tax preview.',
          'Mark each invoice Draft / Sent / Paid and download it as a PDF.',
        ],
        tip: 'Fill in “My Profile” once (name, GSTIN, address) and it auto-fills every future invoice.',
      },
      {
        name: 'Payment & TDS Tracker',
        where: '/payments · Sidebar → TDS Tracker',
        how: [
          '“+ Add Payment” records the brand, amount, financial year, due date and the TDS section (e.g. 194J @10%) — it shows TDS deducted and net receivable.',
          '“Mark Received” logs the amount, date and UTR; payments show Pending / Partial / Received / Overdue.',
          'Summary cards total invoiced, received, pending and TDS deducted (to claim in your ITR).',
        ],
        tip: 'Tick “Received Form 16A” per deal — that’s the TDS certificate you need to claim the deduction at tax time.',
      },
      {
        name: 'Contracts',
        where: '/contracts · Sidebar → Contracts',
        how: [
          'Pick a template (Sponsored, Affiliate, UGC, Brand Ambassador, Custom) and fill the Parties / Deliverables / Payment / Legal tabs.',
          'Covers payment schedule, revisions, exclusivity, usage rights, governing-law state and an optional NDA; a TDS clause is added automatically.',
          'Set a status (Draft/Sent/Signed/Active) and download as PDF.',
        ],
        tip: 'Be specific in Deliverables (format, duration, go-live dates). This is a reference template — consult a lawyer for binding contracts.',
      },
      {
        name: 'Analytics',
        where: '/analytics · Sidebar → Analytics',
        how: [
          '“+ Log Stats” records monthly numbers per platform: followers, views, posts, engagement.',
          'The Overview shows total audience, growth trends (last 6 months) and your best platform.',
        ],
        tip: 'Log stats at month-end from YouTube Studio / Instagram Insights — the app doesn’t pull them automatically.',
      },
      {
        name: 'Media Kit',
        where: '/mediakit · Sidebar → Media Kit',
        how: [
          'Fill the Profile, Platforms, Audience and Brands & Rates tabs.',
          'Save the kit, then download a shareable PDF to pitch brands.',
        ],
        tip: 'Save before downloading — the PDF is built from your saved kit.',
      },
      {
        name: 'Rate Card',
        where: '/ratecard · Sidebar → Rate Card',
        how: [
          'Set per-platform, per-format pricing plus packages and extras.',
          '“Suggest rates” pre-fills values from your niche and average views (2025 India benchmarks) — edit before saving.',
          'Save, then download as PDF.',
        ],
        tip: 'Use “Suggest rates” as a starting point. All rates are treated as exclusive of GST.',
      },
      {
        name: 'Brand Outreach',
        where: '/outreach · Sidebar → Outreach',
        how: [
          'Pick a template (Cold Outreach, Follow-Up, Rate Negotiation, etc.), add brand + your details, and “Generate Email” for a ready subject + body.',
          'Track every pitch in History: Draft → Sent → Replied → Converted.',
        ],
        tip: 'Use the status buttons to see which pitches actually convert.',
      },
    ],
  },
  {
    id: 'planning',
    title: 'Content Planning',
    intro: 'Capture ideas and move every post from idea to published.',
    features: [
      {
        name: 'Ideas',
        where: '/ideas · Sidebar → Ideas',
        how: [
          '“+ New Idea” saves a title, platform, category and notes.',
          'The “AI Idea Generator” suggests titles when you enter a niche.',
          'Toggle ideas “Used” and filter All / Not Used / Used.',
        ],
        tip: 'Mark ideas “Used” after publishing so your vault stays fresh.',
      },
      {
        name: 'Calendar',
        where: '/calendar · Sidebar → Calendar',
        how: [
          '“+ New Post” captures title, platform, status, scheduled date and notes.',
          'Posts move through IDEA → SCRIPTING → FILMING → EDITING → SCHEDULED → POSTED.',
        ],
        tip: 'It’s a status-based production tracker — use the status filters to focus on what’s in progress.',
      },
    ],
  },
  {
    id: 'ai-tools',
    title: 'AI Content Tools',
    intro: 'AI helpers for writing and planning content. There’s a daily limit on how many AI generations you can run.',
    features: [
      {
        name: 'Captions',
        where: '/captions · Sidebar → Captions',
        how: [
          'Choose platform, post type, tone and niche, describe your topic, and “Generate”.',
          'Get a caption, an alternate version, and hashtags (tap any to copy). Everything is saved in History.',
        ],
        tip: 'On Instagram, the app suggests putting hashtags in the first comment to keep captions clean.',
      },
      {
        name: 'Repurpose',
        where: '/repurpose · Sidebar → Repurpose',
        how: [
          'Enter your content title, key points, niche and tone, then “Generate All Versions”.',
          'Produces 8 platform-ready versions: YouTube Short script, IG caption + carousel, Twitter thread, LinkedIn, Facebook, blog outline and newsletter.',
        ],
        tip: 'The more specific your key points, the better each version — personalize in your own voice before posting.',
      },
      {
        name: 'Trending Topics',
        where: '/trending · Sidebar → Trending',
        how: [
          'Pick a niche + platform and “Refresh Ideas” for ~10 AI-curated topics with angles and priority.',
          '“Save to Tracker” keeps the good ones and moves them NOTED → SCRIPTED → PUBLISHED.',
        ],
        tip: 'Ideas don’t auto-refresh — each refresh uses one daily AI generation, so click it only when you want new topics.',
      },
    ],
  },
  {
    id: 'calculators',
    title: 'Free Calculators',
    intro: 'Public tools — no login needed. They calculate live as you type and let you download a PDF where relevant.',
    features: [
      { name: 'Rate Card Generator', where: '/tools/rate-card-generator', how: ['Enter your stats; get per-format price ranges + bundle prices, then download/print.'], tip: 'Uses 2025 India benchmarks — a negotiation starting point, not a fixed price.' },
      { name: 'Brand Deal Price Calculator', where: '/tools/brand-deal-price-calculator', how: ['Choose platform, deliverable, audience size and terms; get a recommended price range.'], tip: 'Prices one deliverable — bundling earns more; charge extra for ad/whitelisting rights.' },
      { name: 'Creator GST & Tax Calculator', where: '/tools/creator-gst-tax-calculator', how: ['Enter annual income; see if GST registration is required (₹20L), GST to charge, and an income-tax estimate.'], tip: 'Indicative only — confirm with a CA before filing.' },
      { name: 'Engagement Rate Calculator', where: '/tools/engagement-rate-calculator', how: ['Enter followers, average likes and comments; get your ER% with a verdict, tier-adjusted for size.'], tip: 'A strong ER lets you charge more than bigger, passive accounts — put it in your media kit.' },
      { name: 'Instagram Money Calculator', where: '/tools/instagram-money-calculator', how: ['Enter niche, deliverable and followers; get per-deliverable rates plus monthly/yearly potential.'], tip: 'Brand deals are the real IG income — a proper rate card + invoice get you taken seriously.' },
      { name: 'YouTube Money Calculator', where: '/tools/youtube-money-calculator', how: ['Choose niche and format, enter monthly views; estimate AdSense + brand-deal upside.'], tip: 'For most creators, brand deals out-earn AdSense several times over.' },
      { name: 'Influencer Invoice Generator', where: '/tools/influencer-invoice-generator', how: ['Enter your details, amount, optional 18% GST and TDS%; preview computes net payable, then download/print.'], tip: 'For more than the odd invoice, the in-app GST Invoices feature stores them and tracks who’s paid.' },
      { name: 'Brand Deal Contract Generator', where: '/tools/brand-deal-contract-generator', how: ['Enter deliverables, fee and terms; build a plain-language agreement to download/print.'], tip: 'A template, not legal advice — have a lawyer review high-value deals.' },
    ],
  },
  {
    id: 'link-in-bio',
    title: 'Link in Bio',
    features: [
      {
        name: 'Your Link-in-Bio page',
        where: '/bio to manage · public page at /bio/your-username · Sidebar → Link-in-Bio',
        how: [
          'Set an avatar, display name, a unique username/slug, bio and theme colour.',
          '“+ Add Link” with title, URL, icon and category; toggle links active/hidden and see per-link click counts.',
          'Copy or preview your public URL.',
        ],
        tip: 'Set links to “Hidden” to temporarily remove them from your public page without deleting them.',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account',
    features: [
      {
        name: 'Your account',
        where: '/account · Sidebar → Account',
        how: [
          'View and manage your profile.',
          'Find support and legal links under “Need help”.',
          'The Danger Zone permanently deletes your account (recoverable for 30 days).',
        ],
        tip: 'Deleting is reversible for 30 days — email support if you change your mind.',
      },
      {
        name: 'Restore a deleted account',
        where: '/restore (from the link in the restore email)',
        how: [
          'Open the link within 30 days of deletion to restore your account and data exactly as you left it.',
        ],
        tip: 'After 30 days, deletion is permanent.',
      },
    ],
  },
];

const CSS = `
  .help-wrap { min-height:100vh; background:#f8faff; font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif; color:#0f172a; }
  .help-hero { max-width:1080px; margin:2.5rem auto 1rem; padding:0 1.5rem; }
  .help-hero h1 { font-family:'Syne',sans-serif; font-size:clamp(1.9rem,3.5vw,2.7rem); font-weight:800; letter-spacing:-0.03em; margin:0 0 .4rem; }
  .help-hero p { color:#64748b; font-size:1.02rem; margin:0; max-width:640px; }
  .help-body { max-width:1080px; margin:1.5rem auto; padding:0 1.5rem 4rem; display:grid; grid-template-columns:230px 1fr; gap:2.5rem; align-items:start; }
  .help-nav { position:sticky; top:1.5rem; }
  .help-nav ul { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.15rem; border-left:2px solid #e2e8f0; }
  .help-nav a { display:block; padding:.4rem .9rem; color:#475569; text-decoration:none; font-size:.9rem; font-weight:600; border-left:2px solid transparent; margin-left:-2px; }
  .help-nav a:hover { color:#6366f1; border-left-color:#6366f1; }
  .help-section { background:white; border:1px solid #f1f5f9; border-radius:16px; padding:1.75rem 2rem; box-shadow:0 1px 3px rgba(0,0,0,0.04); margin-bottom:1.5rem; scroll-margin-top:1.5rem; }
  .help-section > h2 { font-family:'Syne',sans-serif; font-size:1.45rem; font-weight:800; margin:0 0 .35rem; letter-spacing:-0.01em; }
  .help-section > .sec-intro { color:#64748b; font-size:.95rem; margin:0 0 1.25rem; line-height:1.6; }
  .help-feature { padding:1.1rem 0; border-top:1px solid #f1f5f9; }
  .help-feature:first-of-type { border-top:none; padding-top:.25rem; }
  .help-feature h3 { font-size:1.05rem; font-weight:700; color:#0f172a; margin:0 0 .5rem; display:flex; align-items:center; gap:.5rem; }
  .help-where { font-size:.8rem; color:#6366f1; font-weight:600; margin:0 0 .55rem; }
  .help-feature ul { margin:.2rem 0 .6rem; padding-left:1.15rem; }
  .help-feature li { color:#374151; font-size:.92rem; line-height:1.55; margin-bottom:.3rem; }
  .help-tip { font-size:.88rem; color:#374151; background:#f8faff; border:1px solid #e2e8f0; border-radius:10px; padding:.6rem .85rem; }
  .help-tip b { color:#0f172a; }
  .help-foot-note { max-width:1080px; margin:0 auto; padding:0 1.5rem 2.5rem; color:#94a3b8; font-size:.85rem; }
  @media (max-width:820px){ .help-body{ grid-template-columns:1fr; } .help-nav{ position:static; } .help-nav ul{ flex-direction:row; flex-wrap:wrap; border-left:none; gap:.4rem; } .help-nav a{ border:1px solid #e2e8f0; border-radius:999px; margin:0; } }
`;

export default function HelpPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="help-wrap">
      <style>{CSS}</style>
      <TopNav />

      <div className="help-hero">
        <h1>Help Center</h1>
        <p>Everything CreatorKhata can do, and how to use it — feature by feature.</p>
      </div>

      <div className="help-body">
        <nav className="help-nav" aria-label="Help topics">
          <ul>
            {SECTIONS.map(s => (
              <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
            ))}
          </ul>
        </nav>

        <div>
          {SECTIONS.map(section => (
            <section key={section.id} id={section.id} className="help-section">
              <h2>{section.title}</h2>
              {section.intro && <p className="sec-intro">{section.intro}</p>}
              {section.features.map(f => (
                <div className="help-feature" key={f.name}>
                  <h3>{f.name}</h3>
                  {f.where && <p className="help-where">{f.where}</p>}
                  {f.how && (
                    <ul>{f.how.map((h, i) => <li key={i}>{h}</li>)}</ul>
                  )}
                  {f.tip && <div className="help-tip"><b>Tip:</b> {f.tip}</div>}
                </div>
              ))}
            </section>
          ))}
          <p className="help-foot-note">
            Can’t find what you need? Reach us via the <a href="/contact" style={{ color: '#6366f1' }}>Contact</a> page — we’re happy to help.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
